import React, { useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Chat from './Chat';
import LandingPage from './LandingPage';
import './App.css';
import { Analytics } from '@vercel/analytics/react';
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

function App() {
  const [darkMode, setDarkMode] = useState(true);

  const lightTheme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#000',
      },
      background: {
        default: '#ffffff',
        paper: '#fafafa',
        sidebar: '#fafafa',
      },
      text: {
        primary: '#2d2d2d',
        secondary: '#6b7280',
      },
      divider: '#e5e7eb',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--code-bg': 'rgba(0, 0, 0, 0.1)',
            '--pre-bg': 'rgba(0, 0, 0, 0.05)',
            '--blockquote-border': 'rgba(0, 0, 0, 0.2)',
            '--link-color': '#007bff',
            '--hr-color': 'rgba(0, 0, 0, 0.1)',
            '--table-header-bg': 'rgba(0, 0, 0, 0.05)',
            '--table-border': 'rgba(0, 0, 0, 0.1)',
            '--table-row-bg': 'rgba(0, 0, 0, 0.02)',
            '--scrollbar-track': '#f1f1f1',
            '--scrollbar-thumb': '#c1c1c1',
            '--scrollbar-thumb-hover': '#a8a8a8',
            '--thinking-start': '#999',
            '--thinking-middle': '#ddd',
            '--thinking-end': '#999',
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: '#c1c1c1 #f1f1f1',
          },
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: '#a8a8a8',
          },
        },
      },
    },
  });

  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#ffffff66',
      },
      background: {
        default: '#2d2d2d',
        paper: '#1a1a1a',
        sidebar: '#000000',
      },
      text: {
        primary: '#ffffff',
        secondary: '#d1d5db',
      },
      divider: '#333333',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            '--code-bg': 'rgba(255, 255, 255, 0.1)',
            '--pre-bg': 'rgba(255, 255, 255, 0.05)',
            '--blockquote-border': 'rgba(255, 255, 255, 0.2)',
            '--link-color': '#58a6ff',
            '--hr-color': 'rgba(255, 255, 255, 0.1)',
            '--table-header-bg': 'rgba(255, 255, 255, 0.05)',
            '--table-border': 'rgba(255, 255, 255, 0.1)',
            '--table-row-bg': 'rgba(255, 255, 255, 0.02)',
            '--scrollbar-track': '#2d2d2d',
            '--scrollbar-thumb': '#555',
            '--scrollbar-thumb-hover': '#777',
            '--thinking-start': '#666',
            '--thinking-middle': '#999',
            '--thinking-end': '#666',
          },
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: '#555 #2d2d2d',
          },
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            background: '#2d2d2d',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb': {
            background: '#555',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: '#000',
          },
        },
      },
    },
  });

  const theme = darkMode ? darkTheme : lightTheme;

  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
      <ThemeContext.Provider value={{ darkMode, toggleTheme }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/chat" element={<Chat />} />
            </Routes>
            <Analytics />
          </Router>
        </ThemeProvider>
      </ThemeContext.Provider>
    </GoogleOAuthProvider>
  );
}

export default App;
